import axios from "axios";
import { decryptData, encryptData } from "../redux/encryptiondecryption";
import { jwtDecode } from "jwt-decode";

// Create Axios instance
const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_TEST_API_BASE_URL, // Set your base URL here
  headers: {
    "Content-Type": "application/json",
  },
});

// Track whether the refresh token is being requested
let isRefreshing = false;
let failedQueue = [];

// Function to process the failed requests after the token refresh
const processQueue = (error, token = null) => {
  failedQueue.forEach((promise) => {
    if (token) {
      promise.resolve(token);
    } else {
      promise.reject(error);
    }
  });
  failedQueue = [];
  if (token) {
    window.location.reload();
  }
};

// Decode access token to extract expiry time
const decodeAccessToken = (accessToken) => {
  try {
    const decoded = jwtDecode(accessToken);
    if (!decoded.exp) {
      throw new Error("The token does not have an 'exp' field.");
    }
    const expiryTimeInMs = decoded.exp * 1000;
    const expiryDate = new Date(expiryTimeInMs);
    return expiryDate.toLocaleString("en-IN", {
      timeZone: "Asia/Kolkata",
      dateStyle: "medium",
      timeStyle: "medium",
    });
  } catch (error) {
    console.error("Failed to decode access token:", error);
    return null;
  }
};

// Add request interceptor to include the access token in headers
axiosInstance.interceptors.request.use(
  (config) => {
    const otpData = decryptData();
    const accessToken = otpData?.accessToken;

    // Attach access token for all requests except login
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Add response interceptor to handle token expiry and refresh flow
axiosInstance.interceptors.response.use(
  (response) => response, // Handle the response data
  async (error) => {
    const statusCode = error?.response?.status;
    const errorMessage = error?.response?.data?.message;

    // Check if the token is invalid (401 or invalid token message)
    const isTokenInvalid =
      statusCode === 401 || errorMessage === "Token is not valid";

    if (isTokenInvalid) {
      const otpData = decryptData();
      const { refreshToken } = otpData || {};

      // Refresh token flow
      if (!isRefreshing) {
        isRefreshing = true;
        try {
          const refreshResponse = await axios.post(
            `${process.env.REACT_APP_DONOR_API_BASE_URL}/user/refresh-token`, // Use the environment variable for base URL
            { token: refreshToken }
          );

          if (refreshResponse.data.status_code === 1) {
            const newAccessToken = refreshResponse.data.access_token;
            const newRefreshToken = refreshResponse.data.refresh_token;

            // Decode and store the new tokens
            const accessTokenExpiry = decodeAccessToken(newAccessToken);
            const updatedLoginData = {
              accessToken: newAccessToken,
              refreshToken: newRefreshToken,
              accessTokenExpiry,
            };

            encryptData(updatedLoginData); // Save encrypted tokens to localStorage

            // Retry the queued requests with the new access token
            processQueue(null, newAccessToken);

            // Retry the original request with the new access token
            error.config.headers.Authorization = `Bearer ${newAccessToken}`;
            return axios(error.config);
          } else {
            throw new Error("Failed to refresh token");
          }
        } catch (refreshError) {
          processQueue(refreshError);
          localStorage.clear();
          window.location.href = "/";
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }

      // Queue failed requests while refreshing token
      return new Promise((resolve, reject) => {
        failedQueue.push({
          resolve: (token) => {
            error.config.headers.Authorization = `Bearer ${token}`;
            resolve(axios(error.config));
          },
          reject: (err) => {
            reject(err);
          },
        });
      });
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
